import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  authV2MagicSend,
  authV2PasswordLogin,
  setPendingMfa,
  toAuthV2Error,
  toMfaEnrolmentStart,
} from "@/store/slices/authV2Slice";
import type { AppDispatch } from "@/store/store";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { buildPath } from "@/utils/buildPath";
import { completeAuthNavigation } from "@/utils/standaloneConnect";
import { EmailStep } from "./EmailStep";
import { PasswordStep } from "./PasswordStep";

const AuthSignIn = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get("invitationToken") ?? searchParams.get("invitation_token") ?? "";
  const invitedEmail = searchParams.get("email");

  const [email, setEmail] = useState<string | null>(invitedEmail || null);

  const handleBack = () => {
    setEmail(null);
  };

  const handlePasswordSubmit = async (password: string) => {
    const result = await dispatch(
      authV2PasswordLogin({
        email: email!,
        password,
      }),
    );

    if (result.error) {
      const parsedError = toAuthV2Error(result.payload);

      if (parsedError.code === "MFA_REQUIRED" && parsedError.mfa) {
        dispatch(setPendingMfa(parsedError.mfa));
        navigate(buildPath("/auth/mfa", invitationToken ? { invitationToken } : undefined), {
          state: {
            mfa: parsedError.mfa,
            email,
          },
        });
        return;
      }

      // MFA enrolment required to proceed.
      const mfaEnrolmentStart = toMfaEnrolmentStart(result.payload);
      if (parsedError.code === "MFA_ENROLMENT_REQUIRED" && mfaEnrolmentStart) {
        navigate(
          buildPath("/auth/mfa-enrolment", {
            email: email!,
            ...(invitationToken ? { invitationToken } : {}),
          }),
          {
            state: mfaEnrolmentStart,
          },
        );
        return;
      }

      // Multiple organisations detected for the auth user.
      if (parsedError.code === "ORGANISATION_SELECTION_REQUIRED" && (parsedError.organisations ?? []).length > 1) {
        navigate(buildPath("/auth/select-organisation", invitationToken ? { invitationToken } : undefined), {
          state: {
            organisation: parsedError.organisations,
            pendingAuthenticationToken: parsedError.pendingAuthenticationToken ?? "",
          },
        });
        return;
      }

      dispatch(
        openSnackbar({
          message: parsedError.message,
          intent: "danger",
        }),
      );
      return;
    }

    if (invitationToken) {
      navigate(
        buildPath("/auth/invite/accept", {
          invitationToken,
        }),
      );
      return;
    }

    completeAuthNavigation(result.payload);
  };

  const handleMagicSubmit = async () => {
    const payload = {
      email: email!,
      ...(invitationToken ? { invitationToken } : {}),
    };

    const result = await dispatch(authV2MagicSend(payload));
    if (result.error) {
      const parsedError = toAuthV2Error(result.payload);
      dispatch(
        openSnackbar({
          message: parsedError.message,
          intent: "danger",
        }),
      );
      return;
    }

    const nextSearchParams = new URLSearchParams();
    nextSearchParams.set("email", payload.email);
    if (typeof result.payload?.challengeId === "string") {
      nextSearchParams.set("challengeId", result.payload.challengeId);
    }
    if (typeof result.payload?.expiresAt === "string") {
      nextSearchParams.set("expiresAt", result.payload.expiresAt);
    }
    if (invitationToken) {
      nextSearchParams.set("invitationToken", invitationToken);
    }

    dispatch(
      openSnackbar({
        message: "Verification code sent. Check your email.",
        intent: "success",
      }),
    );

    navigate(`/auth/magic?${nextSearchParams.toString()}`);
  };

  return !email ? (
    <EmailStep onContinue={setEmail} />
  ) : (
    <PasswordStep
      email={email}
      onBack={handleBack}
      onPasswordSubmit={handlePasswordSubmit}
      onMagicSubmit={handleMagicSubmit}
    />
  );
};

export { AuthSignIn };
